import React, { Component, ErrorInfo } from "react";
import { AppError, errorUtils } from "../../utils/errors";
import { errorMessageUtils } from "../../utils/errorMessages";
import { errorBoundaryUtils } from "../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card } from "../ui";
import { getStaticPage } from "../../utils/staticPages";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error: AppError | Error;
    errorInfo?: ErrorInfo;
    errorId?: string;
  }>;
  onError?: (error: AppError | Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  className?: string;
  resetOnPropsChange?: boolean;
  resetKeys?: any[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: AppError | Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & { t: (key: string) => string },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { t: (key: string) => string }) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: undefined,
    };
  }

  static getDerivedStateFromError(
    error: AppError | Error,
  ): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: errorUtils.generateErrorId(),
    };
  }

  componentDidCatch(error: AppError | Error, errorInfo: ErrorInfo): void {
    // Log the error
    errorBoundaryUtils.logError(error, {
      timestamp: new Date(),
      severity: errorUtils.getSeverity(
        error instanceof AppError
          ? error
          : new AppError("UNKNOWN_ERROR", error.message),
      ),
      category: errorUtils.getCategory(
        error instanceof AppError ? error.code : "UNKNOWN_ERROR",
      ),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Report the error
    errorBoundaryUtils.reportError(error, {
      timestamp: new Date(),
      severity: errorUtils.getSeverity(
        error instanceof AppError
          ? error
          : new AppError("UNKNOWN_ERROR", error.message),
      ),
      category: errorUtils.getCategory(
        error instanceof AppError ? error.code : "UNKNOWN_ERROR",
      ),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Update state
    this.setState({
      error,
      errorInfo,
      errorId: errorUtils.generateErrorId(),
    });

    // Call custom error handler
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(
    prevProps: ErrorBoundaryProps & { t: (key: string) => string },
  ): void {
    // Reset error state if resetOnPropsChange is true and props have changed
    if (this.props.resetOnPropsChange && this.state.hasError) {
      const propsChanged = this.props.resetKeys?.some(
        (key, index) => key !== prevProps.resetKeys?.[index],
      );
      if (propsChanged) {
        this.resetError();
      }
    }
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: undefined,
    });
  };

  render(): React.ReactNode {
    const {
      children,
      fallback: FallbackComponent,
      showDetails = false,
      className,
      t,
    } = this.props;
    const { hasError, error, errorInfo, errorId } = this.state;

    if (hasError && error) {
      // Use custom fallback component if provided
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={error}
            errorInfo={errorInfo}
            errorId={errorId}
          />
        );
      }

      // Default error UI
      return (
        <div
          className={`min-h-screen flex items-center justify-center p-4 ${className || ""}`}
        >
          <Card className="max-w-2xl w-full">
            <div className="text-center space-y-6">
              {/* Error Icon */}
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              {/* Error Title */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t("errors.somethingWentWrong")}
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  {t("errors.unexpectedError")}
                </p>
              </div>

              {/* Error Details */}
              {showDetails && error && (
                <div className="text-left space-y-4">
                  <Alert variant="error" title={t("errors.errorDetails")}>
                    <div className="space-y-2">
                      <div>
                        <strong>{t("errors.errorId")}:</strong> {errorId}
                      </div>
                      <div>
                        <strong>{t("errors.errorType")}:</strong> {error.name}
                      </div>
                      <div>
                        <strong>{t("errors.errorMessage")}:</strong>{" "}
                        {error.message}
                      </div>
                      {error instanceof AppError && error.code && (
                        <div>
                          <strong>{t("errors.errorCode")}:</strong> {error.code}
                        </div>
                      )}
                      {errorInfo && errorInfo.componentStack && (
                        <div>
                          <strong>{t("errors.componentStack")}:</strong>
                          <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto max-h-32">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </Alert>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.resetError}
                  variant="primary"
                  className="flex-1 sm:flex-none"
                >
                  {t("errors.tryAgain")}
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="flex-1 sm:flex-none"
                >
                  {t("errors.reloadPage")}
                </Button>
                <Button
                  onClick={() => window.history.back()}
                  variant="ghost"
                  className="flex-1 sm:flex-none"
                >
                  {t("errors.goBack")}
                </Button>
              </div>

              {/* Contact Support */}
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <p>
                  {t("errors.contactSupport")}{" "}
                  <a
                    href={getStaticPage("support")?.path || "/support"}
                    className="text-blue-600 hover:text-blue-500 dark:text-blue-400 ml-1"
                  >
                    {getStaticPage("support")?.title || "Contact Support"}
                  </a>
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return children;
  }
}

// Wrapper component to provide translation context
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  const { t } = useTranslation();
  return <ErrorBoundaryClass {...props} t={t} />;
};

// Hook for functional components to handle errors
export const useErrorHandler = () => {
  const { t } = useTranslation();

  const handleError = (error: AppError | Error, context?: any): void => {
    errorBoundaryUtils.logError(error, context);
  };

  async function handleAsyncError<T>(
    promise: Promise<T>,
    context?: any,
  ): Promise<T> {
    try {
      return await promise;
    } catch (error) {
      handleError(error as AppError | Error, context);
      throw error;
    }
  }

  const getErrorMessage = (
    error: AppError | Error,
    language?: string,
  ): string => {
    if (error instanceof AppError) {
      const message = errorMessageUtils.getMessage(error.code, language);
      return errorMessageUtils.formatMessage(message, { field: error.field });
    }
    return error.message || t("errors.unknownError");
  };

  return {
    handleError,
    handleAsyncError,
    getErrorMessage,
    logError: errorBoundaryUtils.logError,
    reportError: errorBoundaryUtils.reportError,
  };
};

// Higher-order component for error handling
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, "children">,
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Error boundary for specific error types
export const withSpecificErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorTypes: string[],
  fallback?: React.ComponentType<{
    error: AppError | Error;
    errorInfo?: ErrorInfo;
    errorId?: string;
  }>,
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary
      fallback={fallback}
      onError={(error) => {
        if (error instanceof AppError && errorTypes.includes(error.code)) {
          // Handle specific error types
          // Log error for debugging in development only
          if (process.env.NODE_ENV === "development") {
          }
        }
      }}
    >
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withSpecificErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};
